// src/hooks/useApi.js
import axios from "axios";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const headersBase = { "Content-Type": "application/json", Accept: "application/json" };

export default function useApi() {
  const navigate = useNavigate();
  const { logoutUser } = useAuth();

  const isFormDataPayload = (payload) => typeof FormData !== "undefined" && payload instanceof FormData;

  // 1) PUBLIC (no token)
  const publicAuth = useCallback((url, method = "GET", payload = null, config = {}) => {
    const formDataPayload = isFormDataPayload(payload);
    return axios({
      baseURL: API_URL,
      url,
      method,
      headers: formDataPayload ? { Accept: "application/json" } : headersBase,
      ...(method === "GET" ? { params: payload } : { data: payload }),
      ...config,
    });
  }, []);

  // 2) LOGOUT
  const doLogout = useCallback(() => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    logoutUser?.();
    navigate("/");
  }, [logoutUser, navigate]);

  // 3) BEARER (with token). Auto-logout on 401.
  const bearerAuth = useCallback(async (url, method = "GET", payload = null, config = {}) => {
    const token = localStorage.getItem("authToken");
    const formDataPayload = isFormDataPayload(payload);
    const headers = {
      ...(formDataPayload ? { Accept: "application/json" } : headersBase),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(config.headers || {}),
    };

    try {
      return await axios({
        baseURL: API_URL,
        url,
        method,
        headers,
        ...(method === "GET" ? { params: payload } : { data: payload }),
        ...config,
      });
    } catch (err) {
      if (err?.response?.status === 401 && token) doLogout();
      throw err?.response?.data || err;
    }
  }, [doLogout]);

  return useMemo(() => ({ publicAuth, bearerAuth, doLogout }), [publicAuth, bearerAuth, doLogout]);
}
